import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { getUser, useGetUserAvatar, useUploadUserAvatar } from '@/common/api/endpoints/users/users';
import { useAuthStore } from '@/common/auth/store';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { useAppToast } from '@/common/hooks/useAppToast';

import { LECTURER_SAVED_PHOTO_PLACEHOLDER } from './LecturerPresenceSetup';

export function useLecturerPresenceSetupUpload(onComplete: () => void) {
  const { t } = useTranslation();
  const toast = useAppToast();
  const uploadUserAvatar = useUploadUserAvatar();

  const {
    data: avatarResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetUserAvatar('me', {
    query: { retry: false },
  });

  const savedPhotoUrl = avatarResponse?.data?.data?.url ?? null;
  const savedPhotoResolving = (isLoading || isFetching) && !isError;
  const savedPhotoLoadFailed = isError;

  const retrySavedPhotoLoad = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  const handlePresenceConfirm = useCallback(async ({
    imageBase64,
  }: {
    imageBase64: string;
    saveAsProfile: boolean;
  }) => {
    if (imageBase64 === LECTURER_SAVED_PHOTO_PLACEHOLDER) {
      onComplete();
      return;
    }
    try {
      await uploadUserAvatar.mutateAsync({
        userId: 'me',
        data: { imageBase64 },
      });
      const userResponse = await getUser('me');
      const userData = userResponse.data.data;
      if (userData) {
        useAuthStore.setState({ user: userData });
      }
      onComplete();
    } catch (error) {
      toast('error', getOralExamApiErrorMessage(error, t('oralExam.actionFailed')));
    }
  }, [onComplete, t, toast, uploadUserAvatar]);

  return {
    savedPhotoUrl,
    savedPhotoResolving,
    savedPhotoLoadFailed,
    retrySavedPhotoLoad,
    handlePresenceConfirm,
  };
}
